"use client";

import { cva } from "class-variance-authority";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  InformationCircleIcon,
  StarIcon,
  TickIcon,
} from "icons";
import React, { useMemo } from "react";
import { Heading, Text } from "ui-components";
import { TooltipComponent } from "ui-components/src/components/Tooltip/TooltipComponent";

import {
  IMembershipsFeatureGroup,
  IMembershipsFeatureItem,
  IMembershipsProps,
} from "@/src/app/[locale]/(public)/about-us/interface/memberships";
import CorporateSolutionsLeadForm from "@/src/components/organization/CorporateSolutionsLeadForm";

const outlineColorMap = {
  essentials: "!outline-membership-blue",
  bronze: "!outline-membership-brown",
  silver: "!outline-membership-silver",
  gold: "!outline-primary",
  platinum: "!outline-membership-dark-gray",
} as const;

const textColor = {
  essentials: "text-membership-blue",
  bronze: "text-membership-brown",
  silver: "text-membership-gray",
  gold: "text-membership-golden",
  platinum: "text-membership-dark-gray",
};

const membershipCardHeading = cva(
  "w-full md:w-[243px] px-4 py-6 bg-white rounded-b-[20px] outline outline-1 -outline-offset-1 flex flex-col justify-start items-start gap-6 rounded-[20px]",
  {
    variants: {
      type: textColor,
    },
    defaultVariants: {
      type: "essentials",
    },
  }
);

const starIconClass = cva("w-4 h-4", {
  variants: {
    type: textColor,
  },
  defaultVariants: {
    type: "essentials",
  },
});

const headingText = cva("!text-[24px] !font-bold", {
  variants: {
    color: textColor,
  },
  defaultVariants: {
    color: "essentials",
  },
});

const reachTextClass = cva("self-stretch text-center font-semibold !text-sm", {
  variants: {
    color: textColor,
  },
  defaultVariants: {
    color: "essentials",
  },
});

const enquireButtonClass = cva("mx-auto w-full !text-sm text-white", {
  variants: {
    color: {
      essentials: "!bg-membership-blue",
      bronze: "!bg-membership-brown",
      silver: "!bg-membership-light-gray",
      gold: "!bg-membership-yellow !text-membership-gray",
      platinum: "!bg-membership-dark-gray",
    },
  },
  defaultVariants: {
    color: "essentials",
  },
});

function MembershipFeatureOption({
  option,
}: {
  option: IMembershipsFeatureItem;
}): JSX.Element {
  return (
    <div className="self-stretch inline-flex justify-end items-start gap-[10px]">
      <div className="w-[14px] h-[14px] relative">
        <TickIcon className="w-2.5 h-2 mt-2" />
      </div>
      <div className="flex-1 text-black text-[14px] font-redhat font-medium">
        {option.text}{" "}
        {option.tooltip && (
          <TooltipComponent
            text={option.tooltip}
            icon={
              <InformationCircleIcon className="inline mt-[-10px] !text-membership-light-gray" />
            }
          />
        )}
      </div>
    </div>
  );
}

export default function MembershipsPage({
  membership,
  isExpanded,
  onToggle,
}: {
  membership: IMembershipsProps;
  isExpanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const classes = useMemo(() => {
    const cardBase = membershipCardHeading({ type: membership.type });
    const outlineClass = outlineColorMap[membership.type];

    return {
      star: starIconClass({ type: membership.type }),
      heading: headingText({ color: membership.type }),
      reach: reachTextClass({ color: membership.type }),
      enquire: enquireButtonClass({ color: membership.type }),
      card: `${cardBase} ${outlineClass} ${
        membership.popular ? "!rounded-t-[0px]" : ""
      }`,
    };
  }, [membership.type, membership.popular]);

  return (
    <div className="w-full h-full flex flex-col justify-start items-start px-2 md:px-0">
      {/* Header */}

      {membership.popular ? (
        <div className="w-full md:w-[243px] p-2.5 bg-primary rounded-t-[20px] justify-center items-center gap-2.5 inline-flex">
          <div className="text-black text-[14px] font-medium break-words">
            MOST POPULAR
          </div>
        </div>
      ) : (
        <div className="w-[98%] p-2.5 justify-center items-center gap-2.5 inline-flex">
          &nbsp;
        </div>
      )}

      {/* Main Card */}
      <div
        data-memberships={membership?.name}
        data-state="Expanded"
        className={classes.card}
      >
        {/* Title Section */}
        <div className="self-stretch h-[100px] px-[13px] py-5 rounded-[6px] flex flex-col justify-center items-center gap-[10px]">
          <div className="w-[130px] flex flex-col justify-start items-center gap-[15px]">
            <div className="w-[105px] flex flex-col justify-start items-center gap-3">
              <div className="inline-flex justify-start items-center gap-[9px]">
                {Array.from({ length: membership.star }, (_, i) => (
                  <StarIcon key={i.toString()} className={classes.star} />
                ))}
              </div>
              <Heading className={classes.heading}>{membership.name}</Heading>
            </div>
            <Text className={classes.reach}>{membership.reach}</Text>
          </div>
        </div>

        {/* CTA */}
        <div className="w-full mx-auto">
          <CorporateSolutionsLeadForm
            intent="primary"
            triggerButtonSize="small"
            triggerButtonText="Enquire now"
            triggerButtonClasses={classes.enquire}
          />
        </div>

        {/* Divider */}
        <div className="w-full h-0 outline outline-1 outline-[#c0c0c0] -outline-offset-[0.5px]" />

        {/* Body */}
        <div className="self-stretch flex-1 flex flex-col justify-between items-start">
          <div className="self-stretch flex flex-col justify-start items-start gap-6 h-[390px] md:h-[430px]">
            <div className="self-stretch text-center text-black text-[14px] font-redhat font-bold">
              {membership?.features[0]?.heading}
            </div>

            {/* Feature list */}
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              {membership?.features[0]?.options.map(
                (option: IMembershipsFeatureItem, idx: number) => (
                  <MembershipFeatureOption
                    key={`feature-0-option-${idx.toString()}`}
                    option={option}
                  />
                )
              )}
            </div>
          </div>

          {/* Toggle Button */}
          <button
            aria-label="Toggle membership details"
            onClick={onToggle}
            className="self-stretch inline-flex justify-between items-center hover:cursor-pointer"
          >
            <div className="text-black text-[14px] font-redhat font-semibold">
              {isExpanded ? "Hide details" : "Show details"}
            </div>
            <div className="relative w-[18px] h-[18px]">
              {isExpanded ? (
                <ArrowUpIcon width="14" height="14" className="text-black" />
              ) : (
                <ArrowDownIcon width="14" height="14" className="text-black" />
              )}
            </div>
          </button>

          {/* Show details */}
          {isExpanded && (
            <div className="self-stretch flex flex-col justify-start items-start gap-6 mt-6">
              {membership?.features
                .slice(1)
                .map((feature: IMembershipsFeatureGroup, fIdx: number) => (
                  <div>
                    <div className="self-stretch text-black text-[14px] font-redhat font-bold">
                      {feature.heading}
                    </div>

                    {/* Feature list */}
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                      {feature?.options.map(
                        (option: IMembershipsFeatureItem, oIdx: number) => (
                          <MembershipFeatureOption
                            key={`feature-${fIdx.toString()}-option-${oIdx.toString()}`}
                            option={option}
                          />
                        )
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
